<template>
  <div class="d-flex flex-column flex-grow-1 rounded p-3">
    <BaseToolBar
      title="Indicateurs matériels"
      subtitle="Statistiques et données sur les matériels et leurs vérifications"
    >
      <div class="btn-group mr-1" role="group">
        <BaseButton
          class="btn btn-primary ml-3"
          v-tooltip="'Exporter les statistiques'"
          @click="exportStats"
          :loading="loading"
          text="Exporter (.xls)"
        ></BaseButton>
      </div>
      <router-link to="/materiel/materiels" class="btn btn-secondary">
        Retour à la liste
      </router-link>
    </BaseToolBar>

    <!-- Loading indicator -->
    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Chargement...</span>
      </div>
    </div>

    <div v-else class="row mt-3">
      <!-- Cartes de statistiques générales -->
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-primary clickable"
          :class="{ active: filtreStatut === null }"
          @click="setFiltreStatut(null)"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-primary">Total des matériels</h5>
            <div class="display-4">{{ materiels ? materiels.length : 0 }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-success clickable"
          :class="{ active: filtreStatut === 'valide' }"
          @click="setFiltreStatut('valide')"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-success">Vérifications à jour</h5>
            <div class="display-4">
              {{ getMaterielsParStatutVerif("valide").length }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-warning clickable"
          :class="{ active: filtreStatut === 'bientot_expire' }"
          @click="setFiltreStatut('bientot_expire')"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-warning">Expire sous 30 jours</h5>
            <div class="display-4">
              {{ getMaterielsParStatutVerif("bientot_expire").length }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 mb-4">
        <div
          class="card h-100 border-danger clickable"
          :class="{ active: filtreStatut === 'expire' }"
          @click="setFiltreStatut('expire')"
        >
          <div class="card-body text-center">
            <h5 class="card-title text-danger">Vérifications expirées</h5>
            <div class="display-4">
              {{ getMaterielsParStatutVerif("expire").length }}
            </div>
          </div>
        </div>
      </div>

      <!-- Tableau des matériels -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div
            class="card-header bg-secondary text-white d-flex justify-content-between align-items-center"
          >
            <div>
              <h5 class="mb-0">{{ getTitreMateriels }}</h5>
            </div>
            <div class="d-flex align-items-center">
              <div class="mr-3 d-flex align-items-center">
                <span class="text-white-50 mr-2">Afficher</span>
                <select
                  v-model="nombreMaterielsAffiches"
                  class="form-control form-control-sm"
                  style="width: auto"
                >
                  <option :value="10">10</option>
                  <option :value="20">20</option>
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                </select>
              </div>
              <button
                v-if="filtreStatut !== null"
                class="btn btn-sm btn-outline-light"
                @click="setFiltreStatut(null)"
              >
                Réinitialiser le filtre
              </button>
            </div>
          </div>
          <div class="card-body">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Identifiant</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>État</th>
                  <th>Dernière vérification</th>
                  <th>Prochaine échéance</th>
                  <th>Statut</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="materiel in derniersMateriels" :key="materiel.id">
                  <td>
                    <span class="badge badge-light">{{
                      materiel.identifiant || "N/A"
                    }}</span>
                  </td>
                  <td>{{ materiel.description || "Sans description" }}</td>
                  <td>{{ getTypeLabel(materiel.materiel_type_id) }}</td>
                  <td>{{ getEtatLabel(materiel.materiel_etat_id) }}</td>
                  <td>
                    <span v-if="getDerniereVerification(materiel.id)">
                      {{
                        formatDate(
                          getDerniereVerification(materiel.id).date_debut
                        )
                      }}
                    </span>
                    <span v-else class="text-muted">Jamais</span>
                  </td>
                  <td>
                    <span v-if="getProchaineEcheance(materiel.id)">
                      {{ formatDate(getProchaineEcheance(materiel.id)) }}
                    </span>
                    <span v-else class="text-muted">N/A</span>
                  </td>
                  <td>
                    <span
                      class="badge"
                      :class="
                        getStatutVerifBadgeClass(
                          getStatutVerification(materiel.id)
                        )
                      "
                    >
                      {{
                        getStatutVerificationLabel(
                          getStatutVerification(materiel.id)
                        )
                      }}
                    </span>
                  </td>
                  <td class="text-center">
                    <router-link
                      :to="`/materiel/materiels`"
                      class="btn btn-sm btn-outline-primary"
                    >
                      <i class="fas fa-eye mr-1"></i> Voir
                    </router-link>
                  </td>
                </tr>
                <tr v-if="derniersMateriels.length === 0">
                  <td colspan="8" class="text-center">
                    {{
                      filtreStatut !== null
                        ? `Aucun matériel avec le statut "${getStatutVerificationLabel(
                            filtreStatut
                          )}" trouvé`
                        : "Aucun matériel enregistré"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Graphique par type de matériel -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-info text-white">
            <h5 class="mb-0">Répartition par type</h5>
          </div>
          <div class="card-body">
            <div v-if="statsParType.length > 0 && data && data.materielTypes">
              <apexchart
                type="donut"
                height="300"
                :options="chartOptionsType"
                :series="statsParType.map(stat => stat.count)"
              ></apexchart>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="fas fa-chart-pie fa-2x mb-2"></i>
              <p>
                {{
                  !data || !data.materielTypes
                    ? "Chargement..."
                    : "Aucune donnée disponible"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique par état -->
      <div class="col-md-6 mb-4">
        <div class="card h-100">
          <div class="card-header bg-warning text-white">
            <h5 class="mb-0">Répartition par état</h5>
          </div>
          <div class="card-body">
            <div v-if="statsParEtat.length > 0 && data && data.materielEtats">
              <apexchart
                type="pie"
                height="300"
                :options="chartOptionsEtat"
                :series="statsParEtat.map(stat => stat.count)"
              ></apexchart>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="fas fa-chart-pie fa-2x mb-2"></i>
              <p>
                {{
                  !data || !data.materielEtats
                    ? "Chargement..."
                    : "Aucune donnée disponible"
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistiques des vérifications -->
      <div class="col-md-12 mb-4">
        <div class="card h-100">
          <div class="card-header bg-success text-white">
            <h5 class="mb-0">Évolution des vérifications (12 derniers mois)</h5>
          </div>
          <div class="card-body">
            <div v-if="statsVerificationsParMois.length > 0">
              <apexchart
                type="line"
                height="350"
                :options="chartOptionsVerifications"
                :series="seriesVerifications"
              ></apexchart>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="fas fa-chart-line fa-2x mb-2"></i>
              <p>Aucune donnée de vérification disponible</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BaseToolBar from "@/components/bases/ToolBar.vue";
import BaseButton from "@/components/bases/Button.vue";
import moment from "moment";

export default {
  name: "IndicateursMateriels",
  components: {
    BaseToolBar,
    BaseButton
  },
  data() {
    return {
      loading: true,
      filtreStatut: null,
      nombreMaterielsAffiches: 20
    };
  },
  computed: {
    ...mapGetters({
      materiels: "materiel/materiels",
      materielsVerifs: "materiel/materielsVerifs",
      data: "materiel/data"
    }),

    derniersMateriels() {
      if (!this.materiels) return [];

      let filtered = this.materiels;

      if (this.filtreStatut !== null) {
        filtered = this.getMaterielsParStatutVerif(this.filtreStatut);
      }

      return filtered.slice(0, this.nombreMaterielsAffiches);
    },

    getTitreMateriels() {
      if (this.filtreStatut === null) {
        return "Tous les matériels";
      }
      return `Matériels - ${this.getStatutVerificationLabel(
        this.filtreStatut
      )}`;
    },

    statsParType() {
      if (!this.materiels) return [];

      const stats = {};
      this.materiels.forEach(materiel => {
        const typeLabel = this.getTypeLabel(materiel.materiel_type_id);
        stats[typeLabel] = (stats[typeLabel] || 0) + 1;
      });

      // Trier par count décroissant et prendre les 10 premiers
      const sortedStats = Object.entries(stats)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);

      if (sortedStats.length <= 10) {
        return sortedStats;
      }

      // Prendre les 10 premiers
      const top10 = sortedStats.slice(0, 10);

      // Calculer la somme des autres
      const autresCount = sortedStats
        .slice(10)
        .reduce((sum, item) => sum + item.count, 0);

      // Ajouter la catégorie "Autres" si nécessaire
      if (autresCount > 0) {
        top10.push({ label: "Autres", count: autresCount });
      }

      return top10;
    },

    statsParEtat() {
      if (!this.materiels) return [];

      const stats = {};
      this.materiels.forEach(materiel => {
        const etatLabel = this.getEtatLabel(materiel.materiel_etat_id);
        stats[etatLabel] = (stats[etatLabel] || 0) + 1;
      });

      return Object.entries(stats).map(([label, count]) => ({
        label,
        count
      }));
    },

    statsVerificationsParMois() {
      if (!this.materielsVerifs) return [];

      const stats = {};
      const now = moment();

      // Initialiser les 12 derniers mois
      for (let i = 11; i >= 0; i--) {
        const month = now
          .clone()
          .subtract(i, "months")
          .format("YYYY-MM");
        stats[month] = 0;
      }

      // Compter les vérifications par mois
      this.materielsVerifs.forEach(verif => {
        if (verif.date_debut) {
          const month = moment(verif.date_debut).format("YYYY-MM");
          if (Object.prototype.hasOwnProperty.call(stats, month)) {
            stats[month]++;
          }
        }
      });

      return Object.entries(stats).map(([month, count]) => ({
        month: moment(month).format("MMM YYYY"),
        count
      }));
    },

    chartOptionsType() {
      return {
        chart: {
          type: "donut"
        },
        labels: this.statsParType.map(stat => stat.label),
        colors: [
          "#007bff",
          "#28a745",
          "#ffc107",
          "#dc3545",
          "#6c757d",
          "#17a2b8"
        ],
        legend: {
          position: "bottom"
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: "bottom"
              }
            }
          }
        ]
      };
    },

    chartOptionsEtat() {
      return {
        chart: {
          type: "pie"
        },
        labels: this.statsParEtat.map(stat => stat.label),
        colors: ["#28a745", "#ffc107", "#dc3545", "#6c757d"],
        legend: {
          position: "bottom"
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: "bottom"
              }
            }
          }
        ]
      };
    },

    chartOptionsVerifications() {
      return {
        chart: {
          type: "line",
          toolbar: {
            show: true
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: "smooth",
          width: 3
        },
        xaxis: {
          categories: this.statsVerificationsParMois.map(stat => stat.month)
        },
        yaxis: {
          title: {
            text: "Nombre de vérifications"
          }
        },
        colors: ["#28a745"],
        grid: {
          borderColor: "#e7e7e7",
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.5
          }
        }
      };
    },

    seriesVerifications() {
      return [
        {
          name: "Vérifications",
          data: this.statsVerificationsParMois.map(stat => stat.count)
        }
      ];
    }
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    ...mapActions({
      _getAll: "materiel/getAll",
      _getData: "materiel/getData"
    }),

    async loadData() {
      this.loading = true;
      try {
        // Charger les données de référence (types, états, etc.)
        await this._getData();
        // Charger les matériels et vérifications
        await this._getAll();
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      } finally {
        this.loading = false;
      }
    },

    setFiltreStatut(statut) {
      this.filtreStatut = statut;
    },

    getMaterielsParStatutVerif(statut) {
      if (!this.materiels) return [];

      return this.materiels.filter(materiel => {
        return this.getStatutVerification(materiel.id) === statut;
      });
    },

    getStatutVerification(materielId) {
      const derniereVerif = this.getDerniereVerification(materielId);
      if (!derniereVerif || !derniereVerif.date_validite) {
        return "aucune";
      }

      const now = moment();
      const dateValidite = moment(derniereVerif.date_validite);
      const diffJours = dateValidite.diff(now, "days");

      if (diffJours < 0) {
        return "expire";
      } else if (diffJours <= 30) {
        return "bientot_expire";
      } else {
        return "valide";
      }
    },

    getDerniereVerification(materielId) {
      if (!this.materielsVerifs) return null;

      const verifs = this.materielsVerifs.filter(
        verif => verif.materiel_id === materielId
      );
      if (verifs.length === 0) return null;

      return verifs.reduce((derniere, current) => {
        if (!derniere) return current;
        if (!current.date_debut) return derniere;
        if (!derniere.date_debut) return current;

        return moment(current.date_debut).isAfter(moment(derniere.date_debut))
          ? current
          : derniere;
      }, null);
    },

    getProchaineEcheance(materielId) {
      const derniereVerif = this.getDerniereVerification(materielId);
      return derniereVerif ? derniereVerif.date_validite : null;
    },

    getTypeLabel(typeId) {
      if (!this.data || !this.data.materielTypes) return "Type inconnu";
      const type = this.data.materielTypes.find(t => t.id === typeId);
      return type ? type.description : "Type inconnu";
    },

    getEtatLabel(etatId) {
      if (!this.data || !this.data.materielEtats) return "État inconnu";
      const etat = this.data.materielEtats.find(e => e.id === etatId);
      return etat ? etat.description : "État inconnu";
    },

    getStatutVerificationLabel(statut) {
      const labels = {
        valide: "À jour",
        bientot_expire: "Expire bientôt",
        expire: "Expiré",
        aucune: "Aucune vérification"
      };
      return labels[statut] || "Inconnu";
    },

    getStatutVerifBadgeClass(statut) {
      const classes = {
        valide: "badge-success",
        bientot_expire: "badge-warning",
        expire: "badge-danger",
        aucune: "badge-secondary"
      };
      return classes[statut] || "badge-secondary";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      return moment(dateString).format("DD/MM/YYYY");
    },

    exportStats() {
      // TODO: Implémenter l'export des statistiques
      console.log("Export des statistiques matériels");
    }
  }
};
</script>

<style scoped>
.clickable {
  cursor: pointer;
  transition: all 0.2s;
}

.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.clickable.active {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.display-4 {
  font-size: 2.5rem;
  font-weight: 300;
}

.chart-container {
  position: relative;
  width: 100%;
}
</style>
